import AppDateInput from '@/app/admin/components/AppDateInput'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Calendar } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import TransferBillingClientsSection from '../../GestionTransferts/Reservations/TransferBillingClientsSection'
import TransferMoralePaymentSection from '../../GestionTransferts/Reservations/TransferMoralePaymentSection'
import TransferPaymentPicker from '../../GestionTransferts/Reservations/TransferPaymentPicker'
import {
  isPartnerOrAffilieUser,
  resolveConnectedPartnerId,
} from '../../GestionTransferts/Reservations/transferBillingIds'

import {
  Button,
  CARD_TYPE,
  Field,
  Modal,
  NumberStepper,
  formatPeriode,
  formatTND,
  hasDailyStock,
  StockAccessInfo,
  toDateInputValue,
  clampCarteBookableDate,
  getMinBookableDateForCarte,
  validateCarteReservationDate,
} from '../shared'
import { buildAssetUrl } from '../../../../../utils/imageUrl'

import {
  EMPTY_BILLING_DRAFT,
  buildCaReservationPayload,
  isAdminUser,
  validateCaBillingDraft,
} from './buildCaReservationPayload'
import { useCaQuotedTotal } from './useCaQuotedTotal'
import { usePartnerPassagerBilling } from '../../../../../utils/partnerPassagerBilling'
import PartnerPassagerClientFields from '../../GestionTransferts/Reservations/PartnerPassagerClientFields'
import CarteDetailsStep from './CarteDetailsStep'
import StepIndicator from './StepIndicator'
import TamarraCarteBookingModal from './TamarraCarteBookingModal'
import { useTamarraParity } from '../../shared/tamarraParity/tamarraParityContext'

const getAccesPaxBounds = (carte) => {
  const maxFromAcces = (Number(carte?.max_adults) || 0) + (Number(carte?.max_children) || 0)
  const max = maxFromAcces > 0 ? maxFromAcces : Number(carte?.max_pax) || 99
  return { min: 1, max: Math.max(1, max) }
}

const LegacyReservationModal = ({ carte, open, defaults, onClose, onConfirm }) => {
  const { t } = useTranslation()
  const [step, setStep] = useState(1)
  const [draft, setDraft] = useState(EMPTY_BILLING_DRAFT)
  const [pax, setPax] = useState(1)
  const [date, setDate] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [formErrors, setFormErrors] = useState({})

  const isAcces = carte?.type === CARD_TYPE.ACCES
  const dailyStock = hasDailyStock(carte)
  const perPax = carte?.pricing_type === 'per_pax'
  const needsReservationStep = isAcces && (dailyStock || perPax)
  const admin = isAdminUser()
  const partnerB2b = isPartnerOrAffilieUser()

  const activeImages = useMemo(
    () => (carte?.images ?? []).filter((img) => img.active && img.url),
    [carte?.images],
  )

  const coverImage = buildAssetUrl(activeImages[0]?.url)

  const setField = useCallback((key, value) => {
    setDraft((d) => ({ ...d, [key]: value }))
  }, [])

  const applyPassagerFields = useCallback((fields) => {
    setDraft((d) => ({ ...d, ...fields }))
  }, [])

  usePartnerPassagerBilling(partnerB2b && open, applyPassagerFields)

  useEffect(() => {
    if (!open) return
    setStep(1)
    setError('')
    setFormErrors({})
    setDraft({ ...EMPTY_BILLING_DRAFT })
    if (!carte) return
    const accesBounds = getAccesPaxBounds(carte)
    const defaultPax = isAcces
      ? Math.min(Math.max(Number(defaults?.pax) || 1, accesBounds.min), accesBounds.max)
      : Math.max(carte.min_pax, Number(defaults?.pax) || carte.min_pax)
    setPax(defaultPax)
    setDate(
      isAcces
        ? clampCarteBookableDate(carte, defaults?.date || carte.start_date)
        : defaults?.date || carte.start_date,
    )
  }, [open, carte, defaults, isAcces])

  const baseTotal = useMemo(() => {
    if (!carte) return 0
    if (perPax) return carte.price * pax
    return carte.price
  }, [carte, perPax, pax])

  const { quotedTotal: total } = useCaQuotedTotal({
    open,
    carte,
    baseAmount: baseTotal,
    pax,
  })

  const paymentDraft = useMemo(
    () => ({
      ...draft,
      amount: total,
      date_pickup: date ? `${date} 00:00:00` : null,
    }),
    [draft, total, date],
  )

  const useMoraleHotelPayment =
    (admin && !partnerB2b && draft.billing_client_kind === 'morale') || partnerB2b

  const stepsAccesShort = useMemo(
    () => [
      { id: 1, label: t('cartesAcces.recherche.modal.stepDetails') },
      { id: 2, label: t('cartesAcces.recherche.modal.stepClient') },
    ],
    [t],
  )

  const stepsWithReservation = useMemo(
    () => [
      { id: 1, label: t('cartesAcces.recherche.modal.stepDetails') },
      { id: 2, label: t('cartesAcces.recherche.modal.stepReservation') },
      { id: 3, label: t('cartesAcces.recherche.modal.stepClient') },
    ],
    [t],
  )

  const steps = useMemo(() => {
    if (!isAcces) return stepsWithReservation
    return needsReservationStep ? stepsWithReservation : stepsAccesShort
  }, [isAcces, needsReservationStep, stepsWithReservation, stepsAccesShort])

  const maxStep = steps.length
  const isReservationStep = step === 2 && (!isAcces || needsReservationStep)
  const isClientStep = isAcces ? (needsReservationStep ? step === 3 : step === 2) : step === 3

  const showDateOnReservationStep = !isAcces || dailyStock
  const showPaxOnReservationStep = !isAcces || perPax

  const paxBounds = useMemo(() => {
    if (!carte) return { min: 1, max: 1 }
    if (isAcces) return getAccesPaxBounds(carte)
    return { min: carte.min_pax, max: carte.max_pax }
  }, [carte, isAcces])

  if (!carte) return null

  const handleNext = () => {
    setError('')
    if (isReservationStep && showDateOnReservationStep) {
      const dateError = validateCarteReservationDate(carte, date, t)
      if (dateError) {
        setError(dateError)
        return
      }
    }
    setStep((s) => Math.min(s + 1, maxStep))
  }

  const handleBack = () => {
    setError('')
    setFormErrors({})
    setStep((s) => Math.max(s - 1, 1))
  }

  const handleConfirm = async () => {
    if (isAcces && hasDailyStock(carte)) {
      const dateError = validateCarteReservationDate(carte, date, t)
      if (dateError) {
        setError(dateError)
        return
      }
    }

    const errors = validateCaBillingDraft(draft, t)
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors)
      setError(Object.values(errors)[0])
      return
    }

    setSubmitting(true)
    try {
      const payload = buildCaReservationPayload(carte, draft, { date, pax, total, isAcces })
      await onConfirm(payload)
    } finally {
      setSubmitting(false)
    }
  }

  const footer =
    step === 1 ? (
      <>
        <Button variant="ghost" onClick={onClose}>
          {t('cartesAcces.recherche.modal.cancel')}
        </Button>
        <Button variant="cta" onClick={handleNext}>
          {t('cartesAcces.recherche.modal.next')}
        </Button>
      </>
    ) : isClientStep ? (
      <>
        <Button variant="ghost" onClick={handleBack} disabled={submitting}>
          {t('cartesAcces.recherche.modal.back')}
        </Button>
        <Button variant="cta" onClick={handleConfirm} loading={submitting}>
          {t('cartesAcces.recherche.modal.confirm')}
        </Button>
      </>
    ) : (
      <>
        <Button variant="ghost" onClick={handleBack}>
          {t('cartesAcces.recherche.modal.back')}
        </Button>
        <Button variant="cta" onClick={handleNext}>
          {t('cartesAcces.recherche.modal.next')}
        </Button>
      </>
    )

  return (
    <Modal open={open} size="2xl" compact title={t('cartesAcces.recherche.modal.title')} onClose={onClose} footer={footer}>
      <StepIndicator steps={steps} current={step} />

      {step === 1 && <CarteDetailsStep carte={carte} />}

      {isReservationStep && (
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3 rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
            {coverImage && (
              <img
                src={coverImage}
                alt={carte.label}
                className="h-12 w-16 flex-shrink-0 rounded-lg object-cover"
              />
            )}
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-gray-800">{carte.label}</p>
              <p className="text-xs text-gray-500">{carte.facility_label}</p>
            </div>
            <div className="ml-auto flex-shrink-0 text-right">
              <p className="text-base font-bold text-brand-700">{formatTND(total)}</p>
            </div>
          </div>

          {showDateOnReservationStep && (
            <div className="flex items-start gap-2.5 rounded-xl border border-gray-100 bg-gray-50 px-3.5 py-2.5 text-sm text-gray-600">
              <Calendar className="mt-0.5 h-4 w-4 shrink-0 text-brand-500" aria-hidden />
              <p className="m-0 leading-relaxed">
                {t('cartesAcces.recherche.modal.validityPeriod')}{' '}
                <span className="font-medium text-gray-800">
                  {formatPeriode(carte.start_date, carte.end_date)}
                </span>
              </p>
            </div>
          )}

          <div className="flex flex-wrap items-start gap-x-6 gap-y-3">
            {showDateOnReservationStep && (
              <Field
                className="w-full max-w-[11.5rem] shrink-0"
                label={t('cartesAcces.recherche.modal.date')}
                required
                error={error && showDateOnReservationStep ? error : undefined}
              >
                <AppDateInput
                  value={date}
                  min={isAcces ? getMinBookableDateForCarte(carte) : toDateInputValue(carte.start_date)}
                  max={toDateInputValue(carte.end_date)}
                  required
                  onChange={(value) => {
                    setDate(value)
                    setError('')
                  }}
                  compact
                />
              </Field>
            )}
            {showPaxOnReservationStep && (
              <Field
                className="shrink-0"
                label={t('cartesAcces.recherche.modal.persons', {
                  min: paxBounds.min,
                  max: paxBounds.max,
                })}
              >
                <NumberStepper
                  value={pax}
                  onChange={setPax}
                  min={paxBounds.min}
                  max={paxBounds.max}
                />
              </Field>
            )}
          </div>

          <StockAccessInfo carte={carte} appearance="banner" />

          <div className="flex items-center justify-between rounded-xl bg-brand-50 px-4 py-3">
            <span className="text-sm font-medium text-gray-500">{t('cartesAcces.recherche.modal.total')}</span>
            <span className="text-2xl font-bold text-brand-700">{formatTND(total)}</span>
          </div>
        </div>
      )}

      {isClientStep && (
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
            <div>
              <p className="text-sm font-semibold text-gray-800">{carte.label}</p>
              <p className="text-xs text-gray-500">{carte.facility_label}</p>
            </div>
            <span className="text-lg font-bold text-brand-700">{formatTND(total)}</span>
          </div>

          {admin && !partnerB2b ? (
            <TransferBillingClientsSection draft={draft} setField={setField} formErrors={formErrors} />
          ) : partnerB2b ? (
            <PartnerPassagerClientFields draft={draft} setField={setField} formErrors={formErrors} />
          ) : null}

          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-gray-900">{t('cartesAcces.recherche.modal.paymentMode')}</h4>
            {useMoraleHotelPayment ? (
              <TransferMoralePaymentSection
                draft={paymentDraft}
                setField={setField}
                partnerIdOverride={partnerB2b ? resolveConnectedPartnerId() : null}
              />
            ) : (
              <TransferPaymentPicker
                totalAmount={total}
                value={draft.payment_bundle}
                onChange={(v) => setField('payment_bundle', v)}
                billingClientId={draft.billing_client_id}
                billingClientKind={draft.billing_client_kind}
                billingPartnerId={draft.billing_partner_id}
                checkInDate={date || null}
              />
            )}
            {formErrors.payment_methods && (
              <p className="text-sm text-red-600">{formErrors.payment_methods}</p>
            )}
          </div>

          {error && !formErrors.payment_methods && (
            <p className="text-sm text-red-600">{error}</p>
          )}
        </div>
      )}
    </Modal>
  )
}

const ReservationModal = (props) => {
  const tamarraParity = useTamarraParity()
  if (tamarraParity) return <TamarraCarteBookingModal {...props} />
  return <LegacyReservationModal {...props} />
}

export default ReservationModal
